import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { InputAdornment, InputBase, makeStyles } from '@material-ui/core';
import { displayErrorMessage, displaySuccess } from 'Helpers/FacetNotificationManager';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import PreferredInstitutionCard from 'Components/UI/PreferredInstitutionCard';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import featureEnabled from 'Helpers/Affordances';
import FinicityConnect from 'Components/UI/FinicityConnect';
import SearchIcon from '@material-ui/icons/Search';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    alignItems: 'center',
  },
  connectOverlay: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    alignItems: 'center',
  },
  progressCircle: {
    marginTop: theme.spacing(10),
  },
  preferredCard: {
    padding: theme.spacing(1),
  },
  addIcon: {
    marginRight: theme.spacing(1),
    padding: theme.spacing(0.25),
  },
  preferredInstitutionContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-evenly',
    width: '100%',
    paddingBottom: theme.spacing(3),
    maxWidth: 1000,
  },
  piTitle: {
    color: theme.palette.facet.primaryGray,
    marginBottom: theme.spacing(2),
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  connectView: {
    height: '100%',
    width: '100%',
  },
  search: {
    padding: theme.spacing(0.5),
    margin: theme.spacing(2),
    backgroundColor: theme.palette.facet.lightGray,
    borderRadius: theme.radius.lg,
    color: theme.palette.facet.accentBlue,
    width: theme.dimensions.sm,
    display: 'none', // <--- Brock (12/16/2020) - PLEASE NOTE: Hidden until search func covers all institutions (currently just Finicity)
  },
  searchInput: {
    textAlign: 'center',
    color: theme.palette.primary.main,
    fontSize: 20,
    fontWeight: theme.typography.fontWeightRegular,
  },
}));

function LinkExternalAccount({
  onComplete,
  onCancel,
  finicityLinkUrl,
}) {
  const classes = useStyles();
  const { householdResource } = useSimpleHousehold();
  const [institutionCollection, setInstitutionCollection] = useState(null);
  const [preferredInstitutions, setPreferredInstitutions] = useState([]);
  const [showFinicityConnect, setShowFinicityConnect] = useState(false);
  const [finicityConnectUrl, setFinicityConnectUrl] = useState(finicityLinkUrl);
  const [loading, setLoading] = useState(false);
  const [currentInstitutionName, setCurrentInstitutionName] = useState('');

  useEffect(() => {
    if (featureEnabled(householdResource, 'preferredInstitutions')) {
      setLoading(true);
      householdResource.relationship('preferredInstitutions')
        .link('related')
        .Get()
        .then((instResponse) => {
          setInstitutionCollection(instResponse);
          setPreferredInstitutions(instResponse.resources().list());
          setLoading(false);
        })
        .catch((err) => {
          setLoading(false);
          displayErrorMessage(err, 'Failed to fetch preferred institutions');
        });
    }
  }, [householdResource]);

  useEffect(() => {
    setFinicityConnectUrl(finicityConnectUrl);
  }, [finicityLinkUrl]);

  useEffect(() => {
    if (finicityConnectUrl) {
      setShowFinicityConnect(true);
      setLoading(false);
    }
  }, [finicityConnectUrl]);

  useEffect(
    () => () => {
      window.finicityConnect?.destroy(); // eslint-disable-line
    },
    [],
  );
  const handleSearch = (e) => {
    const val = e.target.value.toUpperCase();
    const institutions = institutionCollection.resources().list();
    setPreferredInstitutions(institutions.filter(
      inst => inst.attr('institution').toUpperCase().includes(val),
    ));
  };

  const initiateLink = (instResource) => {
    setCurrentInstitutionName(instResource.attr('institution'));
    instResource.relationship('generateConnectUrl')
      .link('related')
      .Get()
      .then((resp) => {
        setFinicityConnectUrl(
          resp.resources().head().attr('url'),
        );
      })
      .catch((err) => {
        setShowFinicityConnect(false);
        displayErrorMessage(err, 'Error generating preferred account link');
      });
  };

  return (
    <>
      <div className={classes.root}>
        {loading ? (
          <MuiCircularProgress
            className={classes.progressCircle}
            color="secondary"
          />
        ) : (
          <>
            {showFinicityConnect && (
            <FinicityConnect
              connectUrl={finicityConnectUrl}
              institutionName={currentInstitutionName}
              onSuccess={(newId) => {
                setShowFinicityConnect(false);
                displaySuccess('Successfully linked account');
                onComplete(newId);
              }}
              onFail={() => {
                setShowFinicityConnect(false);
                onComplete();
              }}
              onCancel={() => {
                setShowFinicityConnect(false);
                onCancel();
              }}
            />
            )}
            {!showFinicityConnect && (
              <>
                  {institutionCollection && (
                  <InputBase
                    classes={{
                      root: classes.search,
                      input: classes.searchInput,
                    }}
                    variant="contained"
                    placeholder="Search for it here"
                    onChange={handleSearch}
                    endAdornment={<InputAdornment><SearchIcon color="inherit" /></InputAdornment>}
                  />
                  )}
                <div className={classes.preferredInstitutionContainer}>
                  {preferredInstitutions.map(inst => (
                    <div
                      className={classes.preferredCard}
                      key={`${inst.attr('institution')}-link-card`}
                    >
                      <PreferredInstitutionCard
                        resource={inst}
                        onClick={initiateLink}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}

LinkExternalAccount.defaultProps = {
  onComplete: () => {},
  finicityLinkUrl: null,
  onCancel: () => {},
};

LinkExternalAccount.propTypes = {
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
  finicityLinkUrl: PropTypes.string,
};

export default LinkExternalAccount;
